// Product art for the back office (manual §10 illustration, §11 "cut-outs on colour"). A product
// without a photo shows the drawing that fits its name, else the one for its category (Fresh
// category, Grocery shelf, grill menu section), else its line's fallback, always on the line's
// category colour. Mirrors productArtName() in apps/customer/src/brand-ui/productArt.tsx; the
// names here are the files in public/illustrations/.

import { ART_ON_FIELD } from './art-on-field.js';

/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */

/**
 * Name keywords → art file. First match wins, so the specific rules sit above the general ones
 * ("Sardines in tomato sauce" is a tin, not a tomato; "Garlic rice" is a bowl, not a bulb or a sack).
 * @type {readonly (readonly [RegExp, string])[]}
 */
const ART_RULES = [
  // packs and tins first: their names list the things inside
  [/\balmusal\b|\bbreakfast (pack|bundle)\b/i, 'almusal'],
  [/\bsardin(e|es|as)\b|\bcanned fish\b|\bmackerel\b|\bligo\b/i, 'sardines'],
  [/\bcorned\b|\bcorn(ed)? ?beef\b|\bluncheon meat\b|\bmeat ?loaf\b|\bspam\b/i, 'corned-beef'],
  // grill
  [/\bhot ?dogs?\b|\bsausages?\b|\blonganisa\b|\blongganisa\b/i, 'hotdog'],
  [/\bskewers?\b|\bbbq\b|\bbarbecue\b|\bisaw\b|\bbetamax\b|\bkwek/i, 'skewer'],
  [/\b(java|garlic|fried|plain|steamed|sinangag) rice\b|\brice (meal|bowl|combo|cup)\b|\bsinangag\b|\bkanin\b/i, 'rice-bowl'],
  [/\bliempo\b|\bpork belly\b|\bbelly\b|\blechon kawali\b|\bbacon\b/i, 'liempo'],
  [/\bchicken\b|\binasal\b|\bmanok\b/i, 'chicken-leg'],
  [/\ba(t)?ch?ara\b|\batsara\b|\bpickles?\b|\bpickled\b/i, 'atsara'],
  [/\biced? ?tea\b|\btea\b|\bjuice\b|\bgulaman\b|\blemonade\b/i, 'iced-tea'],
  // seafood
  [/\bpusit\b|\bsquid\b|\bcalamares\b/i, 'squid'],
  [/\bhipon\b|\bshrimps?\b|\bprawns?\b|\bsugpo\b/i, 'shrimp'],
  [/\bbangus\b|\bfish\b|\btuna\b|\btilapia\b|\bgalunggong\b|\bisda\b|\blapu|\bmaya-maya\b|\bsalmon\b|\bdilis\b/i, 'fish'],
  // grocery
  [/\beggs?\b|\bitlog\b/i, 'egg-tray'],
  [/\brice\b|\bbigas\b/i, 'rice-bag'],
  [/\bsoda\b|\bcoke\b|\bcola\b|\bpepsi\b|\bsprite\b|\broyal\b|\bsoft ?drinks?\b/i, 'soda'],
  [/\bkape\b|\bcoffee\b|\b3-in-1\b|\bnescaf/i, 'coffee'],
  [/\bpancit\b|\bcanton\b|\bnoodles?\b|\bmami\b|\bmiki\b|\bbihon\b|\bsotanghon\b|\bramen\b|\blucky me\b/i, 'noodles'],
  [/\basukal\b|\bsugar\b/i, 'sugar'],
  [/\bmantika\b|\bcooking oil\b|\bvegetable oil\b|\bpalm oil\b|\boil\b/i, 'cooking-oil'],
  [/\btoyo\b|\bsoy\b|\bsoya\b/i, 'soy-sauce'],
  [/\bsabon\b|\bsoaps?\b|\bbody wash\b/i, 'soap'],
  // vegetables and fruit
  [/\bkangkong\b|\bpechay\b|\bpetsay\b|\bbok ?choy\b|\bmalunggay\b|\bgreens\b|\blettuce\b|\bspinach\b|\bcabbage\b|\brepolyo\b/i, 'greens'],
  [/\btomato(es)?\b|\bkamatis\b/i, 'tomato'],
  [/\bmangga\b|\bmangoe?s?\b/i, 'mango'],
  [/\bsibuyas\b|\bonions?\b|\bshallots?\b/i, 'onion'],
  [/\bbawang\b|\bgarlic\b/i, 'garlic'],
  [/\btalong\b|\beggplants?\b|\baubergines?\b/i, 'eggplant'],
  [/\bkarot\b|\bcarrots?\b/i, 'carrot'],
  [/\bampalaya\b|\bbitter (gourd|melon)\b|\bamargoso\b/i, 'ampalaya'],
  [/\bk?calamansi\b|\bkalamansi\b|\bcalamondin\b|\blimes?\b/i, 'calamansi'],
];

/**
 * Category → art, for a product whose name matches nothing. `line` narrows a rule to one line.
 * @type {readonly (readonly [RegExp, string, ProductLine?])[]}
 */
const CATEGORY_RULES = [
  [/\bdrinks?\b|\bbeverages?\b/i, 'iced-tea', 'grill'],
  [/\bseafood\b|\bfish\b/i, 'fish'],
  [/\bvegetables?\b|\bgulay\b/i, 'greens'],
  [/\bfruits?\b|\bprutas\b/i, 'mango'],
  [/\binihaw\b|\bgrill(ed)?\b|\bbbq\b/i, 'skewer'],
  [/\bmeals?\b|\bsides?\b|\brice\b/i, 'rice-bowl'],
  [/\bdrinks?\b|\bbeverages?\b/i, 'soda'],
  [/\bcanned\b|\bcans?\b/i, 'corned-beef'],
  [/\bnoodles?\b/i, 'noodles'],
  [/\beggs?\b/i, 'egg-tray'],
  [/\bhousehold\b|\bpersonal care\b|\btoiletries\b/i, 'soap'],
  [/\bpantry\b|\bcondiments?\b/i, 'rice-bag'],
];

/** The line's own drawing when nothing else fits (the drawing carries its category field). */
export const FALLBACK_ART = { fresh: 'fallback-fresh', grill: 'fallback-grill', mart: 'fallback-grocery' };

/**
 * The art file for a product (name keywords, then its category), or null when only the line's
 * fallback fits.
 * @param {string} name
 * @param {ProductLine} [line]
 * @param {string | null} [category]
 * @returns {string | null}
 */
export function productArtName(name, line, category) {
  const byName = ART_RULES.find(([re]) => re.test(name || ''));
  if (byName) return byName[1];
  if (category) {
    const byCategory = CATEGORY_RULES.find(([re, , only]) => (!only || only === line) && re.test(category));
    if (byCategory) return byCategory[1];
  }
  return null;
}

/**
 * Where a photo-less product's art lives: its drawing, else the line fallback. Unknown lines fall
 * back to Grocery's bag.
 * @param {string} name
 * @param {ProductLine | null | undefined} line
 * @param {string | null} [category]
 */
export function productArtFile(name, line, category) {
  const art = productArtName(name, line ?? undefined, category);
  const file = art ?? FALLBACK_ART[line ?? 'mart'] ?? FALLBACK_ART.mart;
  // On its line's tile, a drawing with a fill of that colour uses its .on-<field> variant (the
  // skewer's Ember chunks on the Grill tile), as the apps' Illustration `on` does.
  const field = LINE_FIELD[line ?? 'mart'];
  const variants = /** @type {Record<string, string[]>} */ (ART_ON_FIELD);
  return art && variants[art]?.includes(field) ? `illustrations/${art}.on-${field}.svg` : `illustrations/${file}.svg`;
}

/** Each line's tile colour (the field product art sits on). */
const LINE_FIELD = { fresh: 'kiwi', grill: 'ember', mart: 'sky' };

/**
 * The §10 state scenes the Partner Hub uses for empty and error states. Every key is a file in
 * public/illustrations/.
 */
export const SCENES = {
  noOrders: 'no-orders',
  noTickets: 'no-tickets',
  noProducts: 'no-products',
  noResults: 'no-results',
  emptyCart: 'empty-cart',
  error: 'error-oops',
  offline: 'offline',
  notFound: 'not-found',
  arrival: 'arrival-burst',
  delivered: 'delivered',
  fresh: 'onboarding-fresh',
  grill: 'onboarding-grill',
};

/** @typedef {keyof typeof SCENES} SceneName */

/** @param {SceneName} scene */
export function sceneFile(scene) {
  return `illustrations/${SCENES[scene]}.svg`;
}
